import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip
} from '@mui/material';
import StandardDialog from '../base/standardDialog';
import SessionInfoDisplay from './SessionInfoDisplay';
import { AutoNegoState, VehicleAutoNegoSession } from '../../../models/vehicleAutoNegoSession';
import { AutoSearchMode, VehicleAutoSearch } from '../../../models/vehicleAutoSearch';
import { startAutoNego, endAutoNego, getAutoNegoStatusColor, getAutoNegoShortStatusLabel } from '../../../services/autoSessionService';
import { SubscriptionRequiredError } from '../../../services/apiClient';
import { useSubscriptionDialog } from '../../../context/subscriptionDialogContext';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

const AUTONEGO_PARAMS_STORAGE_KEY = 'notea-autonego-params';

interface AutoNegoDialogProps {
  open: boolean;
  vehicleId: number;
  session: VehicleAutoNegoSession | null;
  autoSearchSession?: VehicleAutoSearch | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AutoNegoDialog: React.FC<AutoNegoDialogProps> = ({
  open,
  vehicleId,
  session,
  autoSearchSession,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showSubscriptionDialog } = useSubscriptionDialog();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdownMinutes, setCountdownMinutes] = useState<number>(0);

  // Form state
  const [maxConcurrentNegos, setMaxConcurrentNegos] = useState<number>(3);
  const [targetSuccessfulNegos, setTargetSuccessfulNegos] = useState<number>(2);
  const [autoRespond, setAutoRespond] = useState<boolean>(false);
  const [searchMode, setSearchMode] = useState<AutoSearchMode>(AutoSearchMode.PREFERRED_DESTINATIONS);
  const [shouldReturnToRegCountry, setShouldReturnToRegCountry] = useState<boolean>(false);
  const [syncWithAutoSearch, setSyncWithAutoSearch] = useState<boolean>(true);

  // Get the auto-search mode to sync with
  const autoSearchMode = autoSearchSession?.autoSearchMode ?? AutoSearchMode.PREFERRED_DESTINATIONS;
  const autoSearchShouldReturn = autoSearchSession?.shouldReturnRegCountry ?? false;

  // Initialize form with session data
  useEffect(() => {
    if (!open) return;

    const isActiveSession = session && [AutoNegoState.ON, AutoNegoState.WAITING_RESULTS, AutoNegoState.RESULT_TO_REVIEW].includes(session?.autoNegoState ?? AutoNegoState.OFF);

    if (isActiveSession) {
      // Active session - use session parameters
      setMaxConcurrentNegos(session.maxConcurrentNegos ?? 3);
      setTargetSuccessfulNegos(session.targetSuccessfulNegos ?? 2);
      setAutoRespond(session.autoRespond ?? false);
      setSearchMode(session.searchMode ?? AutoSearchMode.PREFERRED_DESTINATIONS);
      setShouldReturnToRegCountry(session.shouldReturnRegCountry ?? false);
    } else {
      // No active session - try to restore from localStorage
      const savedParams = localStorage.getItem(AUTONEGO_PARAMS_STORAGE_KEY);
      if (savedParams) {
        try {
          const params = JSON.parse(savedParams);
          setMaxConcurrentNegos(params.maxConcurrentNegos ?? 3);
          setTargetSuccessfulNegos(params.targetSuccessfulNegos ?? 2);
          setAutoRespond(params.autoRespond ?? false);
          setSearchMode(params.searchMode ?? AutoSearchMode.PREFERRED_DESTINATIONS);
          setShouldReturnToRegCountry(params.shouldReturnToRegCountry ?? false);
          setSyncWithAutoSearch(params.syncWithAutoSearch ?? true);
        } catch (e) {
          console.error('Failed to parse saved auto-nego params:', e);
          // Fallback to defaults
          setMaxConcurrentNegos(3);
          setTargetSuccessfulNegos(2);
          setAutoRespond(false);
          setSearchMode(AutoSearchMode.PREFERRED_DESTINATIONS);
          setShouldReturnToRegCountry(false);
          setSyncWithAutoSearch(true);
        }
      } else {
        // No saved params - use defaults
        setMaxConcurrentNegos(3);
        setTargetSuccessfulNegos(2);
        setAutoRespond(false);
        if (autoSearchSession) {
          setSearchMode(autoSearchMode);
          setShouldReturnToRegCountry(autoSearchShouldReturn);
          setSyncWithAutoSearch(true);
        } else {
          setSearchMode(AutoSearchMode.PREFERRED_DESTINATIONS);
          setShouldReturnToRegCountry(false);
          setSyncWithAutoSearch(true);
        }
      }
    }
    setError(null);
  }, [session, open, autoSearchMode, autoSearchShouldReturn, autoSearchSession]);

  const handleSave = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // If session exists, end it first (without canceling negotiations)
      if (session) {
        await endAutoNego(vehicleId, {
          cancel_negotiations: false,
          send_email: false,
        });
      }

      // Start new session with updated parameters
      await startAutoNego(vehicleId, {
        max_concurrent_negos: maxConcurrentNegos,
        target_successful_negos: targetSuccessfulNegos,
        search_mode: searchMode,
        should_return_reg_country: shouldReturnToRegCountry,
      });

      // Save parameters to localStorage for future use
      localStorage.setItem(AUTONEGO_PARAMS_STORAGE_KEY, JSON.stringify({
        maxConcurrentNegos,
        targetSuccessfulNegos,
        autoRespond,
        searchMode,
        shouldReturnToRegCountry,
        syncWithAutoSearch,
      }));

      onSuccess();
      onClose();
    } catch (err: any) {
      // Handle subscription errors
      if (err instanceof SubscriptionRequiredError) {
        showSubscriptionDialog(err.subscriptionType);
        onClose();
        return;
      }

      const errorKey = err?.errorData?.error?.additionalInfo || err?.errorData?.error?.errorKey;
      switch (errorKey) {
        case 'client.extended.noAutoNegoSubscription':
          setError(t('No auto negotiation subscription.'));
          break;
        case 'client.extended.autoNegoAlreadyStarted':
          setError(t('Auto negotiation is already in progress.'));
          break;
        case 'client.body.auth':
          setError(t('Authentication failed.'));
          break;
        case 'internal.db':
          setError(t('A server error occurred. Please try again later.'));
          break;
        default:
          setError(t('Failed to start auto negotiation session'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await endAutoNego(vehicleId, {
        cancel_negotiations: false,
        send_email: false,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || t('Failed to stop auto negotiation'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return t('Never');
    const d = new Date(date);
    return d.toLocaleString();
  };


  // Check if session is currently ON
  const isSessionOn = session && [AutoNegoState.ON, AutoNegoState.WAITING_RESULTS, AutoNegoState.RESULT_TO_REVIEW].includes(session?.autoNegoState ?? AutoNegoState.OFF);

  // Update countdown timer
  useEffect(() => {
    if (!isSessionOn || !session?.toCloseAt) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const closeTime = new Date(session.toCloseAt).getTime();
      const remainingMs = closeTime - now;
      const remainingMinutes = Math.max(0, Math.ceil(remainingMs / (1000 * 60)));
      setCountdownMinutes(remainingMinutes);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 30000);
    return () => clearInterval(interval);
  }, [isSessionOn, session?.toCloseAt]);

  // Handle sync checkbox change
  const handleSyncChange = (checked: boolean) => {
    setSyncWithAutoSearch(checked);
    // When enabling sync, also update search mode and shouldReturn to match auto-search
    if (checked) {
      setSearchMode(autoSearchMode);
      setShouldReturnToRegCountry(autoSearchShouldReturn);
    }
  };

  // Handle search mode change - untick sync checkbox when manually changed
  const handleSearchModeChange = (mode: AutoSearchMode) => {
    setSearchMode(mode);
    // Untick sync checkbox when user manually changes search mode
    if (syncWithAutoSearch && mode !== autoSearchMode) {
      setSyncWithAutoSearch(false);
    }
  };

  // Check if any values have changed from the original session values
  const hasChanges = (): boolean => {
    if (!isSessionOn) {
      // For new sessions, always allow starting
      return true;
    }
    // Compare current form values with session values
    const originalMaxConcurrent = session?.maxConcurrentNegos ?? 3;
    const originalTargetSuccessful = session?.targetSuccessfulNegos ?? 2;
    const originalAutoRespond = session?.autoRespond ?? false;
    const originalSearchMode = session?.searchMode ?? AutoSearchMode.PREFERRED_DESTINATIONS;
    const originalShouldReturn = session?.shouldReturnRegCountry ?? false;

    return (
      maxConcurrentNegos !== originalMaxConcurrent ||
      targetSuccessfulNegos !== originalTargetSuccessful ||
      autoRespond !== originalAutoRespond ||
      searchMode !== originalSearchMode ||
      shouldReturnToRegCountry !== originalShouldReturn
    );
  };

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={isSessionOn ? t('Edit Auto Negotiation') : t('Start Auto Negotiation')}
      subtitle={t('Configure automatic negotiation parameters')}
      width={{ xs: '95%', sm: '85%', md: '550px', lg: '650px' }}
      autoSize={true}
      error={error}
      headerActions={
        isSessionOn ? (
          <Button
            variant="outlined"
            color="error"
            onClick={handleStop}
            disabled={loading}
            size="small"
          >
            {t('Stop Auto Nego')}
          </Button>
        ) : undefined
      }
      primaryAction={{
        label: isSessionOn ? t('Save Changes') : t('Start Auto Nego'),
        onClick: handleSave,
        loading: loading,
        disabled: loading || !hasChanges(),
      }}
      secondaryAction={{
        label: t('Cancel'),
        onClick: onClose,
        disabled: loading,
      }}
      contentSx={{ p: { xs: 2, sm: 2.5, md: 3 } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Search Mode */}
        <FormControl fullWidth>
          <InputLabel>{t('Search Mode')}</InputLabel>
          <Select
            value={searchMode}
            label={t('Search Mode')}
            onChange={(e) => handleSearchModeChange(e.target.value as AutoSearchMode)}
            disabled={loading}
          >
            <MenuItem value={AutoSearchMode.CIRCLE_TRIP}>
              {t('Circle Trip')}
            </MenuItem>
            <MenuItem value={AutoSearchMode.PREFERRED_DESTINATIONS}>
              {t('Preferred Destinations')}
            </MenuItem>
            <MenuItem value={AutoSearchMode.ANY}>
              {t('Any Country')}
            </MenuItem>
          </Select>
          { searchMode === AutoSearchMode.CIRCLE_TRIP &&
            <Box sx={{ mt: 1, color: 'text.secondary' }}>
              <Typography variant="body2">
                {t("Searches for offers along the vehicle's assigned route, supporting circular paths. Ideal for vehicles that follow a defined route with automatic return.")}
              </Typography>
            </Box>
          }
          { searchMode === AutoSearchMode.PREFERRED_DESTINATIONS &&
            <Box sx={{ mt: 1, color: 'text.secondary' }}>
              <Typography variant="body2">
                {t('Searches for offers going through your configured preferred destinations. Uses priority scores to choose optimal destinations.')}
              </Typography>
            </Box>
          }
          { searchMode === AutoSearchMode.ANY &&
            <Box sx={{ mt: 1, color: 'text.secondary' }}>
              <Typography variant="body2">
                {t('Searches for offers to any destination without restrictions. Suitable for vehicles without specific route preferences or when maximum flexibility is desired.')}
              </Typography>
            </Box>
          }
          
          {/* Should Return to Reg Country - Only for Preferred Destinations */}
          {searchMode === AutoSearchMode.PREFERRED_DESTINATIONS && (
            <FormControlLabel
              control={
                <Switch
                  checked={shouldReturnToRegCountry}
                  onChange={(e) => {
                    setShouldReturnToRegCountry(e.target.checked);
                    // Untick sync when user manually changes this
                    if (syncWithAutoSearch && e.target.checked !== autoSearchShouldReturn) {
                      setSyncWithAutoSearch(false);
                    }
                  }}
                  disabled={loading}
                />
              }
              label={
                <Box>
                  <Typography component="span">{t('Search offers back to registration country')}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('(Requires return destinations to be configured in the Destinations tab of Settings.)')}
                  </Typography>
                </Box>
              }
            />
          )}

          {/* Auto-search sync checkbox */}
          {autoSearchSession && (
            <Box sx={{ mt: 1.5 }}>
              <Tooltip
                title={t(
                  "When enabled, Search Mode follows this vehicle's auto-search settings. Uncheck to override manually."
                )}
                enterDelay={300}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncWithAutoSearch}
                      onChange={(e) => handleSyncChange(e.target.checked)}
                      size="small"
                      sx={{
                        color: syncWithAutoSearch ? 'text.black' : 'error.main',
                        '&.Mui-checked': {
                          color: 'text.black'
                        }
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: (theme: any) => theme.typography.fontSizes.body,
                        color: syncWithAutoSearch ? 'text.darkGrey' : 'error.main'
                      }}
                    >
                      {t('Sync with auto-search session')}
                    </Typography>
                  }
                />
              </Tooltip>
            </Box>
          )}
        </FormControl>

        <Box sx={{ my: 1, borderBottom: '1px solid', borderColor: 'divider' }} />

        {/* Max Concurrent Negotiations */}
        <TextField
          label={t('Max Concurrent Negotiations')}
          type="number"
          value={maxConcurrentNegos}
          onChange={(e) => setMaxConcurrentNegos(parseInt(e.target.value) || 3)}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          disabled={loading}
          fullWidth
          inputProps={{ min: 1 }}
          helperText={
            session && (session.currentActiveNegos ?? 0) >= maxConcurrentNegos && (session.currentActiveNegos ?? 0) > 0
              ? t('Active negotiations have reached the maximum, please increase the limit to start more.')
              : t('Maximum number of simultaneous negotiations')
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              ...(session && (session.currentActiveNegos ?? 0) >= maxConcurrentNegos && (session.currentActiveNegos ?? 0) > 0 && {
                '& fieldset': {
                  borderColor: 'warning.main',
                  borderWidth: '3px',
                },
                '&:hover fieldset': {
                  borderColor: 'warning.main',
                },
              }),
            },
            '& .MuiFormHelperText-root': {
              color: session && (session.currentActiveNegos ?? 0) >= maxConcurrentNegos && (session.currentActiveNegos ?? 0) > 0 ? 'warning.main' : 'inherit',
              fontWeight: session && (session.currentActiveNegos ?? 0) >= maxConcurrentNegos && (session.currentActiveNegos ?? 0) > 0 ? 500 : 'normal',
            },
          }}
        />

        {/* Target Successful Negotiations */}
        <TextField
          label={t('Target Successful Negotiations')}
          type="number"
          value={targetSuccessfulNegos}
          onChange={(e) => setTargetSuccessfulNegos(parseInt(e.target.value) || 2)}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          disabled={loading}
          fullWidth
          inputProps={{ min: 1 }}
          helperText={
            session && (session.successfulNegosCount ?? 0) >= targetSuccessfulNegos && (session.successfulNegosCount ?? 0) > 0
              ? t('✓ Target successful negotiations reached')
              : t('Stop after reaching this many successful negotiations')
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              ...(session && (session.successfulNegosCount ?? 0) >= targetSuccessfulNegos && (session.successfulNegosCount ?? 0) > 0 && {
                '& fieldset': {
                  borderColor: 'success.main',
                  borderWidth: '3px',
                },
                '&:hover fieldset': {
                  borderColor: 'success.main',
                },
              }),
            },
            '& .MuiFormHelperText-root': {
              color: session && (session.successfulNegosCount ?? 0) >= targetSuccessfulNegos && (session.successfulNegosCount ?? 0) > 0 ? 'success.main' : 'inherit',
              fontWeight: session && (session.successfulNegosCount ?? 0) >= targetSuccessfulNegos && (session.successfulNegosCount ?? 0) > 0 ? 500 : 'normal',
            },
          }}
        />

        {/* Auto Respond Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={autoRespond}
              onChange={(e) => setAutoRespond(e.target.checked)}
              disabled={true}
            />
          }
          label={
            <Box>
              <Typography component="span">{t('Enable Auto Respond')}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('(Coming soon - automatically respond to negotiation emails)')}
              </Typography>
            </Box>
          }
        />

        <Box sx={{ my: 1, borderBottom: '1px solid', borderColor: 'divider' }} />

        {/* Display-only fields if session exists */}
        {session && (
          <SessionInfoDisplay
            statusMessage={
              session.statusMessage && session.autoNegoState !== AutoNegoState.OFF
                ? session.statusMessage
                : undefined
            }
            statusColor={getAutoNegoStatusColor(theme, session)}
            currentStatus={session.autoNegoState}
            fields={[
              {
                label: t('Status: '),
                value: getAutoNegoShortStatusLabel(t, session.autoNegoState),
                color: getAutoNegoStatusColor(theme, session),
                bold: true
              },
              {
                label: t('Started At:'),
                value: formatDate(session.startedAt),
              },
              {
                label: t('Current Active:'),
                value: session.currentActiveNegos ?? 0,
              },
              {
                label: t('Successful Count:'),
                value: session.successfulNegosCount ?? 0,
              },
              {
                label: t('Failed Count:'),
                value: session.failedNegosCount ?? 0,
              },
              {
                label: t('Time Remaining:'),
                value: `${countdownMinutes} ${t('minutes')}`,
                color: theme.palette.text.purple,
                bold: true,
                showForStatus: [AutoNegoState.ON, AutoNegoState.WAITING_RESULTS, AutoNegoState.RESULT_TO_REVIEW],
              },
              ...(session.endedAt
                ? [
                  {
                    label: t('Ended At:'),
                    value: formatDate(session.endedAt),
                  },
                ]
                : []),
            ]}
          />
        )}
      </Box>
    </StandardDialog>
  );
};

export default AutoNegoDialog;
